/**
 * Consult Door B — the route wrapper mounted by App.tsx (PUBLIC, no auth).
 *
 *   /book/type/:slug  → evergreen public link (blank form)
 *   /book/:token      → record-specific link (pre-filled, editable)
 *   /book/manage?t=…  → the emailed / WhatsApp "manage booking" link. Carries the
 *                       signed reschedule token in the `t` query param; we seed a
 *                       booking with that token and open the reschedule/cancel
 *                       screen so self-serve reschedule + cancel reach the backend
 *                       (the token is all `reschedulePublicBooking` /
 *                       `cancelPublicBooking` need). Without this the link matched
 *                       `/book/:token` as token="manage" and `?t=` was dropped —
 *                       the reschedule token stayed null and the actions no-opped.
 *
 * `?preview=1` swaps the live App-Check-gated callables for the mock provider
 * so the page can be walked end-to-end before the Cloud Functions deploy
 * (dev/QA only — the same discipline as the portal's /preview-* routes). In
 * live mode, if the callables can't be reached the page shows the honest
 * loading/error state — never a fake booking.
 */
import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import BookConsult from './BookConsult';
import { makeMockBookingApi } from '../lib/publicBookingApiMock';
import type { BookedInfo } from '../lib/publicBookingApi';

export default function BookConsultRoute() {
  const { token, slug } = useParams<{ token?: string; slug?: string }>();
  const [sp] = useSearchParams();
  const preview = sp.get('preview') === '1';

  // The emailed manage link carries the signed reschedule token as `?t=`.
  const manageToken = (sp.get('t') ?? '').trim() || undefined;

  // A fresh mock per mount so the "slot just taken" one-shot resets on reload.
  const mock = useMemo(() => (preview ? makeMockBookingApi() : undefined), [preview]);

  // Seed a minimal booking so the reschedule/cancel handlers have the token.
  // The slot times aren't in the URL (the token is opaque), so the page degrades
  // the "your call right now" line gracefully — reschedule/cancel only need the
  // token to reach the backend.
  const managedBooking = useMemo<BookedInfo | null>(
    () =>
      manageToken
        ? {
            consultationId: '',
            status: 'confirmed',
            startISO: '',
            endISO: '',
            meetUrl: null,
            rescheduleToken: manageToken,
          }
        : null,
    [manageToken],
  );

  return (
    <BookConsult
      token={token}
      typeSlug={slug}
      apiOverride={mock}
      {...(managedBooking
        ? { initialScreen: 'reschedule' as const, initialBooking: managedBooking }
        : {})}
    />
  );
}
